"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PropertyDef = void 0;
const observer_set_1 = require("./observer-set");
const error_1 = require("./error");
/**
 * Defines an observable property.
 */
class PropertyDef {
    /**
     * @param subjectDef the owner of this property
     * @param key key of the property this defines
     * @param originalDescriptor original property descriptor for the property, if
     * 	one exists.
     */
    constructor(subjectDef, key, originalDescriptor, _options) {
        this.subjectDef = subjectDef;
        this.key = key;
        this.originalDescriptor = originalDescriptor;
        /**
         * Observers registered for this property, mapped by instance.
         */
        this.observers = new WeakMap();
        this.name = `${subjectDef.name}.${String(key)}`;
        const descriptor = this.originalDescriptor;
        if (!descriptor) {
            /*
                Without a descriptor, we have a simple value property.
                https://www.typescriptlang.org/docs/handbook/decorators.html#property-decorators.

                Overwrite the instance's original property with a new property which
                notifies the instance's property observers on set.
            */
            this.applyToInstance = (instance, source) => {
                this.createObserversFor(instance);
                let value = (source !== null && source !== void 0 ? source : instance)[this.key];
                Object.defineProperty(instance, this.key, {
                    enumerable: true,
                    get: () => value,
                    set: nextValue => {
                        value = nextValue;
                        this.notify(instance);
                    },
                });
            };
        }
        else {
            /*
                With a descriptor, we have an accessor property.
                https://www.typescriptlang.org/docs/handbook/decorators.html#accessor-decorators.
            */
            this.applyToInstance = (instance) => {
                if (!(originalDescriptor === null || originalDescriptor === void 0 ? void 0 : originalDescriptor.set)) {
                    throw new error_1.EcceError(`Cannot use @observable() on a getter without a setter: "${this.subjectDef.name}.${String(this.key)}"`);
                }
                this.createObserversFor(instance);
                // Setter must be bound to instance to allow mutation of #private members.
                const originalSetter = originalDescriptor.set.bind(instance);
                Object.defineProperty(instance, key, Object.assign(Object.assign({}, originalDescriptor), { set: nextValue => {
                        originalSetter(nextValue);
                        this.notify(instance);
                    } }));
            };
        }
    }
    createObserversFor(instance) {
        this.observers.set(instance, new observer_set_1.ObserverSet(this.name));
    }
    getObserversFor(instance) {
        const observers = this.observers.get(instance);
        /* istanbul ignore if */
        if (!observers) {
            /*
                By the time we ever call this method, `applyToInstance` would have been
                invoked on instance, so this should never happen...
            */
            throw new error_1.EcceError('ObservableProperty had no Observers for an instance; probably a bug in ecce...');
        }
        return observers;
    }
    /**
     * Add an observer for this property on the passed instance.
     */
    addObserver(instance, observer, options) {
        this.getObserversFor(instance).addObserver(observer, options);
    }
    /**
     * Remove an observer for this property on the passed instance.
     */
    removeObserver(instance, observer) {
        this.getObserversFor(instance).removeObserver(observer);
    }
    /**
     * Notify this property's observers for the passed instance.
     */
    notify(instance) {
        this.getObserversFor(instance).notify();
        this.subjectDef.notify(instance);
    }
}
exports.PropertyDef = PropertyDef;
//# sourceMappingURL=property-def.js.map